import React from "react";
import { Link } from "react-router-dom";
import { Modal, SwipeableDrawer, useMediaQuery, useTheme, Fade } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import PersonIcon from "@mui/icons-material/Person";

interface SignUpModalProps {
  open: boolean;
  onClose: () => void;
}

const SignUpModal: React.FC<SignUpModalProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const content = (
    <div className="bg-white rounded-t-2xl md:rounded-2xl p-6 md:p-8 w-full max-w-lg mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-[#1E294F]">Choose sign up type</h2>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full transition-colors" aria-label="Close">
          <CloseIcon className="text-[#1E294F]" />
        </button>
      </div>

      {/* Patient Option */}
      <Link
        to="/patient"
        onClick={onClose}
        className="block w-full p-6 mb-4 bg-[#E6D8F6] border-2 border-[#1E294F]/20 rounded-xl hover:border-[#1E294F]/50 hover:shadow-md transition-all group"
      >
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-[#1E294F] rounded-full flex items-center justify-center flex-shrink-0">
            <PersonIcon className="text-white" style={{ fontSize: 32 }} />
          </div>
          <div className="flex-1">
            <h3 className="text-xl md:text-2xl font-bold text-[#1E294F] mb-2 uppercase">Patient Sign Up</h3>
            <p className="text-sm md:text-base text-[#61697D]">{"Sign up with your physician's office"}</p>
          </div>
          <span className="text-[#1E294F] font-bold group-hover:translate-x-1 transition-transform">→</span>
        </div>
      </Link>

      {/* OR Divider */}
      <div className="flex items-center gap-4 my-5">
        <div className="flex-1 h-px bg-[#1E294F]/20"></div>
        <span className="text-lg font-bold text-[#1E294F]">OR</span>
        <div className="flex-1 h-px bg-[#1E294F]/20"></div>
      </div>

      {/* Physician/Clinic Staff Option */}
      <Link
        to="/"
        onClick={onClose}
        className="block w-full p-6 bg-[#D8E8F6] border-2 border-[#1E294F]/20 rounded-xl hover:border-[#1E294F]/50 hover:shadow-md transition-all group"
      >
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-[#1E294F] rounded-full flex items-center justify-center flex-shrink-0">
            <LocalHospitalIcon className="text-white" style={{ fontSize: 32 }} />
          </div>
          <div className="flex-1">
            <h3 className="text-xl md:text-2xl font-bold text-[#1E294F] mb-2 uppercase">Physician Sign Up</h3>
            <p className="text-sm md:text-base text-[#61697D]">Create your practice account</p>
          </div>
          <span className="text-[#1E294F] font-bold group-hover:translate-x-1 transition-transform">→</span>
        </div>
      </Link>
    </div>
  );

  // Mobile: Bottom sheet using SwipeableDrawer
  if (isMobile) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        onOpen={() => {}}
        disableSwipeToOpen
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: "90vh",
          },
        }}
      >
        {/* Drag handle for mobile */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-gray-300 rounded-full"></div>
        </div>
        {content}
      </SwipeableDrawer>
    );
  }

  // Desktop: Centered modal
  return (
    <Modal
      open={open}
      onClose={onClose}
      className="flex justify-center items-center p-4"
      aria-labelledby="signup-modal-title"
      closeAfterTransition
      slotProps={{
        backdrop: {
          timeout: 300,
        },
      }}
    >
      <Fade in={open} timeout={300}>
        <div className="outline-none">{content}</div>
      </Fade>
    </Modal>
  );
};

export default SignUpModal;
